/**
 * Activity-Gamification Mycelium Connector
 *
 * Bridges activity tracking to gamification rewards using mycelium event backbone.
 * Implements natural growth pattern: activities automatically trigger XP/achievements.
 *
 * Key Features:
 * - Automatic XP awards for task/sales activities
 * - Streak tracking for consecutive activities
 * - Team vs individual achievement separation
 * - Milestone cascades (achievement unlocks trigger team events)
 *
 * @module mycelium-activity-gamification
 * @since 2026-02-19
 */

import { myceliumEventBackbone, type MyceliumEvent } from "./mycelium-event-backbone.service";
import { getMyceliumTransport } from "./mycelium-transport-registry";
import { logger } from "../lib/logger";
import { typedSupabase } from "../lib/typed-supabase";

// ============================================================================
// XP REWARD CONFIGURATION
// ============================================================================

/**
 * Activity-to-XP mapping configuration
 * Follows mycelium principle: single source of truth for reward logic
 */
const ACTIVITY_XP_MAP: Record<string, number> = {
  // Task Activities
  task_created: 10,
  task_assigned: 5,
  task_completed: 50,
  task_status_changed: 5,
  task_priority_changed: 3,
  task_comment_added: 2,
  task_mention: 1,

  // Sales Activities
  opportunity_created: 25,
  opportunity_won: 200,
  opportunity_lost: 10, // For effort
  proposal_sent: 30,
  meeting_scheduled: 15,
  follow_up_completed: 10,
  quote_generated: 20,
  contract_signed: 150,

  // Collaboration Activities
  brain_dump_created: 15,
  post_created: 5,
  comment_added: 2,
  reaction_added: 1,

  // Calendar Activities
  calendar_event_created: 5,
  booking_completed: 25,

  // Accounting Activities (Xero integration)
  invoice_created: 40,
  invoice_paid: 100,
  expense_logged: 5,
};

/**
 * Streak bonus multipliers
 */
const STREAK_BONUSES = {
  3: 1.1, // 10% bonus at 3-day streak
  7: 1.25, // 25% bonus at 7-day streak
  14: 1.5, // 50% bonus at 14-day streak
  30: 2.0, // 100% bonus at 30-day streak
  60: 2.5, // 150% bonus at 60-day streak
  90: 3.0, // 200% bonus at 90-day streak
};

/**
 * Milestone thresholds for cascade events
 */
const MILESTONE_THRESHOLDS = [
  100, 250, 500, 1000, 2500, 5000, 10000, 25000, 50000, 100000,
];

// ============================================================================
// CONNECTOR SERVICE
// ============================================================================

export class ActivityGamificationConnector {
  private initialized = false;

  /**
   * Initialize connector and subscribe to activity events
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      logger.debug("ActivityGamificationConnector already initialized");
      return;
    }

    logger.info("🎮 Activity-Gamification connector initializing");

    // Subscribe to activity domain events
    myceliumEventBackbone.subscribe({
      name: "ActivityGamificationConnector",
      domains: ["task", "sales", "calendar", "accounting"],
      handler: this.handleActivityEvent.bind(this),
      priority: 8, // High priority to ensure XP is awarded quickly
    });

    this.initialized = true;
    logger.info("✅ Activity-Gamification connector ready");
    logger.info("💡 Using existing database streak tracking and award_xp_with_season() function");
  }

  /**
   * Handle activity events and award XP
   */
  private async handleActivityEvent(event: MyceliumEvent): Promise<void> {
    const { type, userId, payload } = event;

    try {
      if (!userId) {
        logger.debug("Skipping event without userId", { eventId: event.id, type });
        return;
      }

      // Check if this activity type awards XP
      const baseXP = ACTIVITY_XP_MAP[type];
      if (!baseXP) {
        logger.debug("No XP mapping for activity type", { type });
        return;
      }

      // Get user's current streak multiplier from database
      const streakMultiplier = await this.getStreakMultiplier(userId);
      const totalXP = Math.floor(baseXP * streakMultiplier);

      logger.debug("💎 Awarding XP for activity", {
        userId,
        activityType: type,
        baseXP,
        streakMultiplier,
        totalXP,
        eventId: event.id,
      });

      // Award XP (database handles streak tracking)
      await this.awardXP(userId, totalXP, type, payload);

      // Check for milestone achievements
      await this.checkMilestones(userId, event);
    } catch (error) {
      logger.error("❌ ActivityGamification handler error", {
        eventType: type,
        eventId: event.id,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  }

  /**
   * Award XP to user using existing database infrastructure
   */
  private async awardXP(
    userId: string,
    xp: number,
    activityType: string,
    details: Record<string, unknown>
  ): Promise<void> {
    try {
      // Use award_xp_with_season function from database
      // This integrates with existing streak calculation, daily/weekly/monthly tracking
      const { error } = await typedSupabase.rpc("award_xp_with_season", {
        user_id: userId,
        xp_amount: xp,
        transaction_type: activityType,
        task_id: (details.taskId as string) || (details.sourceId as string) || null,
        details: details ? JSON.parse(JSON.stringify(details)) : null,
      });

      if (error) {
        logger.error("Failed to award XP via award_xp_with_season", { userId, xp, activityType, error });
        return;
      }

      logger.info("XP awarded successfully", { userId, xp, activityType });

      // Publish XP award event back to mycelium for other services
      await getMyceliumTransport().publish({
        domain: "gamification",
        type: "xp_awarded",
        userId,
        payload: {
          xp,
          activityType,
          source: "ActivityGamificationConnector",
        },
      });
    } catch (error) {
      logger.error("Error awarding XP", {
        userId,
        xp,
        activityType,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  }

  /**
   * Get streak multiplier from database
   * Database already tracks streaks, we just apply the bonus
   */
  private async getStreakMultiplier(userId: string): Promise<number> {
    try {
      const { data, error } = await typedSupabase
        .from("profiles")
        .select("current_streak")
        .eq("id", userId)
        .single();

      if (error || !data) {
        logger.warn("Could not fetch user streak, defaulting to 1.0x", { userId, error });
        return 1.0;
      }

      const streak = data.current_streak || 0;
      const multiplier = this.getStreakMultiplierFromCount(streak);

      if (multiplier > 1.0) {
        logger.info("🔥 Streak bonus active", {
          userId,
          streak,
          multiplier,
        });
      }

      return multiplier;
    } catch (error) {
      logger.error("Error getting streak multiplier", {
        userId,
        error: error instanceof Error ? error.message : "Unknown error",
      });
      return 1.0;
    }
  }

  /**
   * Get multiplier for current streak count
   */
  private getStreakMultiplierFromCount(streak: number): number {
    // Find highest applicable streak bonus
    const applicableBonuses = Object.entries(STREAK_BONUSES)
      .filter(([threshold]) => streak >= parseInt(threshold))
      .map(([_, multiplier]) => multiplier);

    return applicableBonuses.length > 0
      ? Math.max(...applicableBonuses)
      : 1.0;
  }

  /**
   * Check if user has reached any milestones
   */
  private async checkMilestones(userId: string, event: MyceliumEvent): Promise<void> {
    try {
      // Fetch user's current total XP
      const { data, error } = await typedSupabase
        .from("profiles")
        .select("xp, points")
        .eq("id", userId)
        .single();

      if (error || !data) {
        logger.warn("Could not fetch user XP for milestone check", { userId, error });
        return;
      }

      // Use xp or points (both exist for backward compat)
      const totalXP = data.xp || data.points || 0;
      const activityXP = ACTIVITY_XP_MAP[event.type] || 0;

      // Check if user just crossed a milestone threshold
      for (const threshold of MILESTONE_THRESHOLDS) {
        if (totalXP >= threshold && (totalXP - activityXP) < threshold) {
          // User just crossed this milestone!
          logger.info("🎯 Milestone achieved!", {
            userId,
            threshold,
            totalXP,
          });

          // Publish milestone event for team notifications, achievements, etc.
          await getMyceliumTransport().publish({
            domain: "gamification",
            type: "milestone_achieved",
            userId,
            payload: {
              milestone: threshold,
              totalXP,
              triggeringActivity: event.type,
              source: "ActivityGamificationConnector",
            },
          });

          // Only trigger for first milestone crossed
          break;
        }
      }
    } catch (error) {
      logger.error("Error checking milestones", {
        userId,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  }

  /**
   * Cleanup on disposal
   */
  async dispose(): Promise<void> {
    this.initialized = false;
    logger.info("🧹 Activity-Gamification connector disposed");
  }

}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

export const activityGamificationConnector = new ActivityGamificationConnector();
